import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { 
  Bot, 
  BrainCircuit,
  Database,
  Workflow,
  Globe
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export const LayerArchitecture = () => {
  const [hoveredLayer, setHoveredLayer] = useState(null);
  const [activeFlow, setActiveFlow] = useState(false);
  const { t } = useLanguage();

  const layers = [
    {
      id: 5,
      name: t('layers.layer5.name'),
      description: t('layers.layer5.description'),
      icon: Bot,
      isDoora: false,
    },
    {
      id: 4,
      name: t('layers.layer4.name'),
      description: t('layers.layer4.description'),
      icon: BrainCircuit,
      isDoora: true,
    },
    {
      id: 3,
      name: t('layers.layer3.name'),
      description: t('layers.layer3.description'),
      icon: Database,
      isDoora: true,
    },
    {
      id: 2,
      name: t('layers.layer2.name'),
      description: t('layers.layer2.description'),
      icon: Workflow,
      isDoora: true,
    },
    {
      id: 1,
      name: t('layers.layer1.name'),
      description: t('layers.layer1.description'),
      icon: Globe,
      isDoora: true,
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setActiveFlow(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Connection line component
  const ConnectionLine = ({ index }) => (
    <motion.div
      className="absolute left-1/2 -translate-x-1/2 w-px h-6 -top-6"
      initial={{ scaleY: 0, opacity: 0 }}
      animate={{ scaleY: 1, opacity: 1 }}
      transition={{ delay: 0.5 + index * 0.15, duration: 0.3 }}
    >
      <div className="w-full h-full bg-gradient-to-b from-transparent via-blue-400/40 to-blue-400/20" />
    </motion.div>
  );

  return (
    <div className="relative w-full h-full min-h-[480px] flex items-center justify-center" data-testid="layer-architecture">
      {/* Layer Stack */}
      <div className="relative w-full max-w-md px-4">
        {layers.map((layer, index) => {
          const Icon = layer.icon;
          const isHovered = hoveredLayer === layer.id;
          
          return (
            <motion.div
              key={layer.id}
              className="relative"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index, duration: 0.5 }}
            >
              {/* Connection line */}
              {index > 0 && <ConnectionLine index={index} />}
              
              <motion.div
                className={`relative mb-2.5 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
                  layer.isDoora ? 'ring-1 ring-blue-400/40' : ''
                }`}
                onMouseEnter={() => setHoveredLayer(layer.id)}
                onMouseLeave={() => setHoveredLayer(null)}
                whileHover={{ scale: 1.02, x: 8 }}
                style={{
                  boxShadow: isHovered 
                    ? '0 4px 20px rgba(59, 130, 246, 0.15)' 
                    : layer.isDoora 
                      ? '0 2px 12px rgba(59, 130, 246, 0.1)' 
                      : '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                {/* Layer background */}
                <div className={`absolute inset-0 ${layer.isDoora ? 'bg-blue-900/20' : 'bg-zinc-950'}`} />
                
                {/* Doora indicator */}
                {layer.isDoora && (
                  <motion.div
                    className="absolute -left-0.5 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full"
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                )}

                {/* Content */}
                <div className="relative p-3.5 flex items-center gap-3.5 border border-zinc-800/60 rounded-xl">
                  {/* Icon */}
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    layer.isDoora 
                      ? 'bg-gradient-to-br from-blue-500 to-indigo-500' 
                      : 'bg-zinc-100'
                  }`}>
                    <Icon className={`w-4.5 h-4.5 ${layer.isDoora ? 'text-white' : 'text-zinc-400'}`} />
                  </div>

                  {/* Text */}
                  <div className="flex-grow min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-medium tracking-wider text-zinc-400 uppercase">
                        {t('layers.layerLabel')} {layer.id}
                      </span>
                      {layer.isDoora && (
                        <span className="px-1.5 py-0.5 rounded bg-blue-500 text-[8px] font-bold text-white uppercase tracking-wider">
                          Doora
                        </span>
                      )}
                    </div>
                    <h4 className={`text-sm font-semibold ${layer.isDoora ? 'text-blue-400' : 'text-zinc-200'}`}>
                      {layer.name}
                    </h4>
                    <p className="text-xs text-zinc-400 truncate">{layer.description}</p>
                  </div>

                  {/* Flow indicator */}
                  {activeFlow && (
                    <motion.div
                      className={`w-1.5 h-1.5 rounded-full ${layer.isDoora ? 'bg-blue-500' : 'bg-zinc-300'}`}
                      animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.5, 1, 0.5],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.2,
                      }}
                    />
                  )}
                </div>
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default LayerArchitecture;
